import { z } from 'zod';
import { IsoTime, Severity, ServerId } from './basics.js';
/**
 * The daily summary (knowledge doc section 13; P9.3).
 *
 * Two halves. **The facts** are counted from the database — events by severity,
 * category and server, what is still unacknowledged, servers offline, bans, AI
 * suggestions waiting, the worst events — so a summary goes out every day whether
 * or not AI is on, and nothing in it is a model's guess. **The narrative** is the
 * model's reading of those facts, when a model is set up and answers: a headline,
 * a paragraph, what stood out and what is worth a look. It is labelled as such
 * wherever it is shown.
 */
export const SummaryEvent = z.object({
    id: z.uuid(),
    severity: Severity,
    category: z.string(),
    serverName: z.string(),
    message: z.string(),
    sourceIp: z.string().nullable(),
    occurredAt: IsoTime,
});
export const SUMMARY_TOP_EVENTS = 15;
export const DailySummaryStats = z.object({
    windowStart: IsoTime,
    windowEnd: IsoTime,
    total: z.number().int(),
    bySeverity: z.record(Severity, z.number().int()),
    byCategory: z.array(z.object({ category: z.string(), count: z.number().int() })).max(10),
    byServer: z
        .array(z.object({ serverId: ServerId, serverName: z.string(), count: z.number().int() }))
        .max(10),
    /** High and critical events in the window nobody has acknowledged yet. */
    unacknowledgedSerious: z.number().int(),
    offlineServers: z.array(z.object({ id: ServerId, name: z.string() })).max(20),
    bans: z.number().int(),
    pendingSuggestions: z.number().int(),
    /** The worst events, newest first within a severity. */
    top: z.array(SummaryEvent).max(SUMMARY_TOP_EVENTS),
});
/** The model's output for `summarize_events`: text for a person, no action. */
export const SummaryNarrative = z
    .object({
    headline: z.string().min(1).max(300),
    summary: z.string().max(2500),
    highlights: z.array(z.string().min(1).max(400)).max(6),
    concerns: z.array(z.string().min(1).max(400)).max(6),
})
    .strict();
export const SummaryDeliveryView = z.object({
    ruleId: z.uuid(),
    ruleName: z.string(),
    status: z.enum(['pending', 'sent', 'failed']),
    attempts: z.number().int(),
});
export const DailySummaryView = z.object({
    /** The UTC date the window ends on. */
    day: z.string(),
    stats: DailySummaryStats,
    narrative: SummaryNarrative.nullable(),
    providerName: z.string().nullable(),
    model: z.string().nullable(),
    /** Why there is no narrative, when there is none. */
    aiNote: z.string().nullable(),
    createdAt: IsoTime,
    deliveries: z.array(SummaryDeliveryView),
});
/** `GET /notifications/summary/preview`: the facts for the last day, as a summary would carry them. */
export const SummaryPreview = z.object({ stats: DailySummaryStats, text: z.string() });
//# sourceMappingURL=daily-summary.js.map